import React, { useContext, useEffect } from 'react';
import { ProgressCircle } from '@wds/progress-circle';
import { FiveHundredErrorPage, StaticContext } from '@sm/webassets';
import { t, T } from '@sm/intl';

import getQueryParam from '~app/helpers/queryParams';
import BaseLoadingPage from '~app/pages/login/BaseLoadingPage';
import { QueryParams } from '~common/entities/smParams';
import { SamlErrorCode } from '~common/errors/userFacing';
import COPY, { getErrorMessage } from './copy';
import { fetchSamlConnection, removeSamlConnection } from '~app/helpers/sessionStorage';
import samlConnectionApi from '~app/helpers/samlConnectionApi';

export const LOADING_TEST_ID = 'LoadingPage';

export function getSamlErrorCode(errorReason: string | undefined): SamlErrorCode | undefined {
  if (errorReason && Object.values<string>(SamlErrorCode).includes(errorReason)) {
    return errorReason as SamlErrorCode;
  }

  return undefined;
}

export default function ErrorPage(): JSX.Element {
  const [isLoading, setIsLoading] = React.useState(true);

  const {
    environment: { languageCode },
    pageRequestId,
  } = useContext(StaticContext);
  const errorReason = getQueryParam<string>(QueryParams.ERROR);
  const samlErrorCode = getSamlErrorCode(errorReason);
  const connection = fetchSamlConnection();

  useEffect(() => {
    (async () => {
      const shouldRefreshSamlConnection =
        !!connection &&
        samlErrorCode &&
        (samlErrorCode === SamlErrorCode.X509_MISMATCH || samlErrorCode === SamlErrorCode.X509_CANNOT_VERIFY);

      try {
        if (shouldRefreshSamlConnection) {
          const refreshed = await samlConnectionApi.refreshSamlConnection(connection as string);

          // redirect user back to canonical link if connection was refreshed
          if (refreshed) {
            // Remove the connection from storage before redirecting
            removeSamlConnection();
            window.location.href = samlConnectionApi.getCanonicalLink(connection as string);
            return;
          }
        }
      } catch (e: unknown) {
        // ignore
      }

      // We want to stop showing the spinner if the page is not redirecting
      setIsLoading(false);
    })();
  });

  if (isLoading) {
    return (
      <BaseLoadingPage pageId="LoadingPage">
        <main className="sm-loading--centered" data-testid={LOADING_TEST_ID}>
          <ProgressCircle continuous />
        </main>
      </BaseLoadingPage>
    );
  }

  if (samlErrorCode) {
    const { desc, values } = getErrorMessage(languageCode, samlErrorCode);

    return (
      <FiveHundredErrorPage
        requestId={`${t(COPY.ERROR_CODE)}: ${pageRequestId}`}
        openingMessage={t(COPY.ERROR_TITLE)}
        errorTitle="" // this is to override the default title
        errorMessage={<T desc={desc} html values={values} />}
      />
    );
  }

  return <FiveHundredErrorPage requestId={pageRequestId} />;
}
